import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { getInvoiceByIdOptions } from "@/lib/client/@tanstack/react-query.gen";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
	Receipt,
	Download,
	User,
	Calendar,
	CreditCard,
	AlertTriangle,
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

export const Route = createFileRoute("/facture/$invoiceId")({
	component: InvoiceViewPage,
});

function InvoiceViewPage() {
	const { invoiceId } = Route.useParams();

	const {
		data: invoice,
		isLoading,
		error,
	} = useQuery(
		getInvoiceByIdOptions({
			path: { id: invoiceId },
		}),
	);

	const handlePrint = () => {
		window.print();
	};

	const handleDownloadPDF = () => {
		// En production, ceci devrait appeler un endpoint pour générer un PDF
		const printWindow = window.open("", "_blank");
		if (printWindow) {
			printWindow.document.write(`
        <html>
          <head>
            <title>Facture ${invoice?.invoiceNumber}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              .header { text-align: center; margin-bottom: 30px; }
              .section { margin-bottom: 20px; }
              .flex { display: flex; justify-content: space-between; }
              .text-right { text-align: right; }
            </style>
          </head>
          <body>
            ${document.querySelector(".invoice-content")?.innerHTML}
          </body>
        </html>
      `);
			printWindow.document.close();
			printWindow.print();
		}
	};

	if (isLoading) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
					<p className="mt-4 text-gray-600">Chargement de votre facture...</p>
				</div>
			</div>
		);
	}

	if (error || !invoice) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
				<Card className="w-full max-w-md">
					<CardHeader className="text-center">
						<div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
							<AlertTriangle className="h-6 w-6 text-red-600" />
						</div>
						<CardTitle className="text-xl text-red-800">
							Facture non trouvée
						</CardTitle>
					</CardHeader>
					<CardContent>
						<p className="text-sm text-gray-600 text-center">
							La facture demandée n'existe pas ou vous n'avez pas l'autorisation
							de la consulter.
						</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	const totalAmount = invoice.totalAmount || 0;
	const paidAmount = invoice.paidAmount || 0;
	const remainingAmount = invoice.totalAmount - invoice.paidAmount || 0;

	return (
		<div className="min-h-screen bg-gray-50 py-8 px-4">
			<div className="max-w-4xl mx-auto">
				{/* Actions d'impression (masquées à l'impression) */}
				<div className="mb-6 print:hidden">
					<div className="flex justify-between items-center">
						<h1 className="text-2xl font-bold text-gray-900">
							Facture #{invoice.invoiceNumber}
						</h1>
						<div className="space-x-2">
							<Button variant="outline" onClick={handlePrint}>
								<Receipt className="mr-2 h-4 w-4" />
								Imprimer
							</Button>
							<Button onClick={handleDownloadPDF}>
								<Download className="mr-2 h-4 w-4" />
								Télécharger PDF
							</Button>
						</div>
					</div>
				</div>

				{/* Contenu de la facture */}
				<Card className="invoice-content">
					<CardHeader className="border-b">
						<div className="flex justify-between items-start">
							<div>
								<h1 className="text-3xl font-bold text-gray-900 mb-2">
									REVACOURS
								</h1>
								<p className="text-gray-600">Cours de soutien scolaire</p>
								<div className="mt-4 text-sm text-gray-600">
									<p>Association Revacours</p>
									<p>Adresse de l'association</p>
									<p>Code postal, Ville</p>
									<p>Email: contact@revacours.com</p>
								</div>
							</div>
							<div className="text-right">
								<h2 className="text-2xl font-bold text-gray-900">FACTURE</h2>
								<p className="text-lg font-semibold text-gray-700">
									#{invoice.invoiceNumber}
								</p>
								<div className="mt-4 text-sm">
									<p>
										Date d'émission:{" "}
										{format(new Date(invoice.createdAt || ""), "dd MMMM yyyy", {
											locale: fr,
										})}
									</p>
									<p>
										Date d'échéance:{" "}
										{invoice.dueDate
											? format(new Date(invoice.dueDate), "dd MMMM yyyy", {
													locale: fr,
												})
											: "Non définie"}
									</p>
								</div>
							</div>
						</div>
					</CardHeader>

					<CardContent className="p-6">
						{/* Informations client */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
							<div>
								<h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
									<User className="h-5 w-5" />
									Facturé à
								</h3>
								<div className="text-gray-700">
									<p className="font-medium">
										{invoice.parent?.firstName} {invoice.parent?.lastName}
									</p>
									<p>{invoice.parent?.email}</p>
									<p>{invoice.parent?.phone}</p>
									<p className="mt-2">{invoice.parent?.address}</p>
								</div>
							</div>

							<div>
								<h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
									<Calendar className="h-5 w-5" />
									Période de facturation
								</h3>
								<div className="text-gray-700">
									{invoice.billingPeriodStart && invoice.billingPeriodEnd ? (
										<>
											<p>
												Du{" "}
												{format(
													new Date(invoice.billingPeriodStart),
													"dd MMMM yyyy",
													{ locale: fr },
												)}
											</p>
											<p>
												Au{" "}
												{format(
													new Date(invoice.billingPeriodEnd),
													"dd MMMM yyyy",
													{ locale: fr },
												)}
											</p>
										</>
									) : (
										<p>Période non définie</p>
									)}
								</div>
							</div>
						</div>

						{/* Statut de la facture */}
						<div className="mb-6">
							<div className="flex items-center gap-4">
								<span className="font-medium">Statut:</span>
								<Badge
									variant={
										invoice.status === "PAID"
											? "default"
											: invoice.status === "PENDING"
												? "secondary"
												: invoice.status === "PARTIALLY_PAID"
													? "warning"
													: "destructive"
									}
									className="text-sm"
								>
									{invoice.status === "PAID" && "Payée"}
									{invoice.status === "PENDING" && "En attente"}
									{invoice.status === "PARTIALLY_PAID" && "Partiellement payée"}
									{invoice.status === "FAILED" && "Échec de paiement"}
									{invoice.status === "CANCELLED" && "Annulée"}
								</Badge>
							</div>
						</div>

						<Separator className="my-6" />

						{/* Détails des prestations */}
						<div className="mb-8">
							<h3 className="text-lg font-semibold mb-4">
								Détail des prestations
							</h3>
							<div className="border rounded-lg overflow-hidden">
								<table className="w-full">
									<thead className="bg-gray-50">
										<tr>
											<th className="px-4 py-3 text-left font-medium text-gray-900">
												Description
											</th>
											<th className="px-4 py-3 text-right font-medium text-gray-900">
												Quantité
											</th>
											<th className="px-4 py-3 text-right font-medium text-gray-900">
												Prix unitaire
											</th>
											<th className="px-4 py-3 text-right font-medium text-gray-900">
												Total
											</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-gray-200">
										{/* En production, ces données viendraient de l'API */}
										<tr>
											<td className="px-4 py-3">
												<div>
													<p className="font-medium">
														Cours de soutien scolaire
													</p>
													<p className="text-sm text-gray-600">
														Période:{" "}
														{invoice.billingPeriodStart &&
														invoice.billingPeriodEnd
															? `${format(new Date(invoice.billingPeriodStart), "MMM yyyy", { locale: fr })}`
															: "Période non définie"}
													</p>
												</div>
											</td>
											<td className="px-4 py-3 text-right">1</td>
											<td className="px-4 py-3 text-right">
												{totalAmount.toFixed(2)} €
											</td>
											<td className="px-4 py-3 text-right font-medium">
												{totalAmount.toFixed(2)} €
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>

						{/* Totaux */}
						<div className="border-t pt-4">
							<div className="flex justify-end">
								<div className="w-64">
									<div className="space-y-2">
										<div className="flex justify-between">
											<span>Sous-total:</span>
											<span>{totalAmount.toFixed(2)} €</span>
										</div>
										<div className="flex justify-between">
											<span>TVA (0%):</span>
											<span>0,00 €</span>
										</div>
										<Separator />
										<div className="flex justify-between text-lg font-bold">
											<span>Total:</span>
											<span>{totalAmount.toFixed(2)} €</span>
										</div>

										{paidAmount > 0 && (
											<>
												<div className="flex justify-between text-green-600">
													<span>Montant payé:</span>
													<span>-{paidAmount.toFixed(2)} €</span>
												</div>
												<Separator />
												<div className="flex justify-between text-lg font-bold text-red-600">
													<span>Reste à payer:</span>
													<span>{remainingAmount.toFixed(2)} €</span>
												</div>
											</>
										)}
									</div>
								</div>
							</div>
						</div>

						{/* Informations de paiement */}
						{remainingAmount > 0 && (
							<div className="mt-8 p-4 bg-blue-50 rounded-lg">
								<h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
									<CreditCard className="h-5 w-5" />
									Informations de paiement
								</h3>
								<div className="text-sm text-blue-800">
									<p>
										• Paiement en ligne sécurisé disponible via le lien fourni
									</p>
									<p>
										• Plusieurs modes de paiement acceptés (CB, virement,
										prélèvement)
									</p>
									<p>• Pour toute question: admin@revacours.com</p>
									{invoice.parent && (
										<p className="mt-2">
											<a
												href={`/link/${invoice.parent.id}`}
												className="font-medium underline"
											>
												→ Payer cette facture en ligne
											</a>
										</p>
									)}
								</div>
							</div>
						)}

						{/* Historique des paiements */}
						{invoice.paymentAllocations && invoice.paymentAllocations.length > 0 && (
							<div className="mt-8">
								<h3 className="text-lg font-semibold mb-4">
									Historique des paiements
								</h3>
								<div className="border rounded-lg overflow-hidden">
									<table className="w-full">
										<thead className="bg-gray-50">
											<tr>
												<th className="px-4 py-3 text-left font-medium text-gray-900">
													Date
												</th>
												<th className="px-4 py-3 text-left font-medium text-gray-900">
													Méthode
												</th>
												<th className="px-4 py-3 text-right font-medium text-gray-900">
													Montant
												</th>
												<th className="px-4 py-3 text-left font-medium text-gray-900">
													Statut
												</th>
											</tr>
										</thead>
										<tbody className="divide-y divide-gray-200">
											{invoice.paymentAllocations.map((allocation) => (
												<tr key={allocation.id}>
													<td className="px-4 py-3">
														{allocation.createdAt
															? format(
																	new Date(allocation.createdAt),
																	"dd/MM/yyyy",
																	{ locale: fr },
																)
															: "N/A"}
													</td>
													<td className="px-4 py-3">
														{allocation.paymentMethod === "CARD" &&
															"Carte bancaire"}
														{allocation.paymentMethod === "SEPA" &&
															"Prélèvement SEPA"}
														{allocation.paymentMethod === "BANK_TRANSFER" &&
															"Virement"}
													</td>
													<td className="px-4 py-3 text-right font-medium">
														{allocation.allocatedAmount?.toFixed(2)} €
													</td>
													<td className="px-4 py-3">
														<Badge
															variant={
																allocation.paymentStatus === "SUCCEEDED"
																	? "default"
																	: "destructive"
															}
														>
															{allocation.paymentStatus === "SUCCEEDED" && "Réussi"}
															{allocation.paymentStatus === "FAILED" && "Échec"}
															{allocation.paymentStatus === "PENDING" && "En cours"}
															{allocation.paymentStatus === "REFUNDED" && "Remboursé"}
														</Badge>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>
						)}

						{/* Footer */}
						<div className="mt-8 pt-4 border-t text-center text-sm text-gray-600">
							<p>Merci de votre confiance.</p>
							<p className="mt-2">
								Revacours - Association de soutien scolaire
							</p>
						</div>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
